"use client";

import { createContext, useContext, useEffect } from "react";
import { User } from "./user";

/**
 * Gets the real, server-resolved user down to client components without
 * each of them fetching it themselves. layout.tsx (a Server Component) reads
 * the session once and feeds it in here; everything under it calls
 * useCurrentUser() instead of importing a stub constant.
 */
const UserContext = createContext<User | null>(null);

/**
 * A session expiring mid-session used to be invisible: dozens of scattered
 * fetch() calls across page.tsx and its children would each silently get
 * middleware's 401 (see middleware.ts) and fail their own way — a stale
 * tab, not the login screen. One patch here catches all of them, present
 * and future, without touching every call site.
 */
function useSessionGuard() {
  useEffect(() => {
    const native = window.fetch;
    window.fetch = async (...args) => {
      const res = await native(...args);
      if (res.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
      }
      return res;
    };
    return () => { window.fetch = native; };
  }, []);
}

export function UserProvider({ user, children }: { user: User; children: React.ReactNode }) {
  useSessionGuard();
  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

export function useCurrentUser(): User {
  const u = useContext(UserContext);
  if (!u) throw new Error("useCurrentUser() called outside <UserProvider> — check layout.tsx");
  return u;
}
